import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { type AccountDetails, AccountType } from "@/apis/gql/types";
import { MultiSelectInput } from "@/components/shared/form/multi-select-input";
import { SelectInput } from "@/components/shared/form/select-input";
import { TextAreaInput } from "@/components/shared/form/text-area-input";
import { TextInput } from "@/components/shared/form/text-input";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useUpdateAccount } from "@/queries/accounts/update";

const formSchema = z.object({
  accountId: z.string().min(1).max(255),
  accountNo: z
    .string()
    .min(2, { message: i18next.t("accounts.validation.account_number_min") })
    .max(50, { message: i18next.t("accounts.validation.account_number_max") }),
  accountName: z
    .string()
    .min(2, { message: i18next.t("accounts.validation.account_name_min") })
    .max(120, { message: i18next.t("accounts.validation.account_name_max") }),
  accountDescription: z
    .string()
    .max(600, {
      message: i18next.t("accounts.validation.account_description_max"),
    })
    .nullable(),
  accountType: z.nativeEnum(AccountType),
  min: z.coerce.number().nullable(),
  max: z.coerce.number().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  account: AccountDetails;
};

export const ExpenseAccountDetails = ({ account }: Props) => {
  const { t } = useTranslation();

  const [updateAccount] = useUpdateAccount();

  const handleSubmit = (data: FormValues) => {
    updateAccount(data);
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountId: account.accountId,
      accountNo: account.accountNo,
      accountName: account.accountName,
      accountDescription: account.accountDescription ?? null,
      accountType: account.accountType,
      min: account.min,
      max: account.max,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-2">
        <TextInput name="accountNo" label={t("account_number")} />
        <TextInput name="accountName" label={t("name")} />
        <TextAreaInput
          name="accountDescription"
          label={t("accounts.description.label")}
          className="min-h-[100px]"
        />
        <SelectInput
          name="accountType"
          label={t("accounts.type")}
          options={[
            { value: "ASSET", label: "Anlage" },
            { value: "EXPENSE", label: "Aufwand" },
            { value: "GWG", label: "GWG" },
          ]}
        />
        <div className="flex flex-row gap-2">
          <TextInput type="number" name="min" label={t("min")} />
          <TextInput type="number" name="max" label={t("max")} />
        </div>
        <MultiSelectInput
          modalPopover
          icon={() => null}
          name="specialRules"
          label={t("accounts.special_rules")}
          options={[
            { value: "buExport", label: "Kein BU" },
            { value: "serviceDateExport", label: "Kein Datum" },
            { value: "faktor2", label: "Faktor 2" },
            { value: "automatic", label: "Automatik" },
          ]}
        />
        <div className="flex justify-end mt-2">
          <Button type="submit">{t("save")}</Button>
        </div>
      </form>
    </Form>
  );
};
